import { ApiError } from '../api/client';
import type { CreditChange } from '../api/types';

export function creditUnits(text: string): number | null {
  const match = /^(\d+)(?:\.(\d{1,3}))?$/.exec(text.trim());
  if (!match) return null;
  const units = Number(match[1]) * 1000 + Number((match[2] ?? '').padEnd(3, '0'));
  return Number.isSafeInteger(units) && units > 0 && units <= 9_000_000_000_000 ? units : null;
}
export const formatCredits = (units: number): string =>
  (units / 1000).toLocaleString('en-US', { maximumFractionDigits: 3 });
const storageKey = (orgId: string) => `credit-command-v1:${orgId}`;
type Storage = Pick<globalThis.Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** Persist before sending. A lost response or reload must not mint a second grant. */
export function pendingCreditChange(storage: Storage, orgId: string): CreditChange | null {
  const raw = storage.getItem(storageKey(orgId));
  if (!raw) return null;
  const value: unknown = JSON.parse(raw);
  if (!value || typeof value !== 'object')
    throw new Error('The saved credit request could not be read.');
  const c = value as Record<string, unknown>;
  if (
    typeof c.command_key !== 'string' ||
    typeof c.reason !== 'string' ||
    typeof c.units !== 'number' ||
    !Number.isSafeInteger(c.units) ||
    c.units <= 0 ||
    !(
      (c.kind === 'grant' &&
        (c.reference === null || typeof c.reference === 'string') &&
        (c.expires_at === null || typeof c.expires_at === 'string')) ||
      (c.kind === 'revoke' && typeof c.grant_id === 'string') ||
      (c.kind === 'refund' && typeof c.hold_id === 'string')
    )
  ) {
    throw new Error('The saved credit request could not be read.');
  }
  return value as CreditChange;
}
export function keepCreditChange(storage: Storage, orgId: string, change: CreditChange) {
  storage.setItem(storageKey(orgId), JSON.stringify(change));
}
export function clearCreditChange(storage: Storage, orgId: string) {
  storage.removeItem(storageKey(orgId));
}
export function definitelyRejected(error: unknown): boolean {
  // Authentication failures may follow a committed request whose response was lost.
  // Keep its key across login, rate limiting, timeouts and server errors.
  return error instanceof ApiError && [400, 404, 409, 422].includes(error.status);
}
